import { Fragment, useMemo } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import useTranslations from 'contexts/basicData/useTranslations';
import { useKPIGetTB } from 'api/kpi/kpi';
import { CaseTypeEnum } from 'api/model';
import { maxKPIDateRangeMs } from 'constants/AppConstants';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './management/shared';

type Sums = {
  cases: number;
  price: number;
  cost: number;
  profit: number;
};

const empty = (): Sums => ({ cases: 0, price: 0, cost: 0, profit: 0 });

const Cells = ({ sums }: { sums: Sums }) => (
  <>
    <td style={tdNumStyle}>{nf(sums.cases)}</td>
    <td style={tdNumStyle}>{nf(sums.price)}</td>
    <td style={tdNumStyle}>{nf(sums.cost)}</td>
    <td style={tdNumStyle}>{nf(sums.profit)}</td>
    <td style={tdNumStyle}>
      {pct(sums.price !== 0 ? (sums.profit / sums.price) * 100 : null)}
    </td>
    <td style={tdNumStyle}>
      {sums.cases > 0 ? nf(sums.profit / sums.cases) : '–'}
    </td>
  </>
);

/**
 * TB1 (intäkt minus lön inkl. OB och utlägg) per ärendetyp och län, på samma beräkning som
 * TB-widgeten (KPILogic.GetTB). Länet är ärendets län som i löneunderlaget: från-län på inköp,
 * inbyte, auktion och extra, annars till-län.
 */
const TB1ByCaseTypeAndCountyWidget = () => {
  const { caseTypes } = useTranslations();
  const {
    dashboardState: {
      daterange,
      selectedCaseTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const isDateRangeTooLong =
    !!startDate &&
    !!endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  // Samma parametrar som TBWidget, så att de delar react-querys cache och den tunga
  // prisberäkningen bara körs en gång när båda visas. Servern använder bara period och ärendetyp.
  const getData = useKPIGetTB(
    {
      startDate: startDate!,
      endDate: endDate!,
      caseTypes: selectedCaseTypes,
      supplierIDs: selectedSupplierIDs,
      customerNames: selectedCustomerNames,
      fromCountyIDs: selectedFromCountyIDs,
      toCountyIDs: selectedToCountyIDs,
      excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
    },
    { query: { enabled: !!(startDate && endDate) && !isDateRangeTooLong } }
  );

  const groups = useMemo(() => {
    const byCaseType = new Map<
      CaseTypeEnum,
      { total: Sums; counties: Map<string, Sums> }
    >();
    (getData.data?.dataPoints ?? []).forEach((dp) => {
      const group = byCaseType.get(dp.caseType) ?? {
        total: empty(),
        counties: new Map<string, Sums>(),
      };
      const county = dp.countyName || '(Okänt län)';
      const add = (s: Sums): Sums => ({
        cases: s.cases + dp.totalCases,
        price: s.price + dp.totalPrice,
        cost: s.cost + dp.totalCost,
        profit: s.profit + dp.totalProfit,
      });
      group.counties.set(county, add(group.counties.get(county) ?? empty()));
      group.total = add(group.total);
      byCaseType.set(dp.caseType, group);
    });
    return [...byCaseType.entries()]
      .map(([caseType, group]) => ({
        caseType,
        total: group.total,
        counties: [...group.counties.entries()].sort(
          (a, b) => b[1].profit - a[1].profit
        ),
      }))
      .sort((a, b) => b.total.profit - a.total.profit);
  }, [getData.data]);

  const total = groups.reduce((acc, g) => {
    acc.cases += g.total.cases;
    acc.price += g.total.price;
    acc.cost += g.total.cost;
    acc.profit += g.total.profit;
    return acc;
  }, empty());

  const failedCases = getData.data?.failedCases.length ?? 0;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.TB1ByCaseTypeAndCounty]}
      count={total.cases}
      isLoading={getData.isLoading}
      info="TB1 = intäkt (prislistan) minus lönekostnad (inkl. OB) och utlägg, per ärende — samma beräkning som TB-widgeten. Ärenden stängda i perioden. Länet är ärendets län: från-län på inköp, inbyte, auktion och extra, annars till-län. Bara period och ärendetyp används, som i TB-widgeten."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={groups.length === 0}
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <table style={tableStyle}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th style={thStyle}>Ärendetyp / län</th>
                <th style={thNumStyle}>Ärenden</th>
                <th style={thNumStyle}>Intäkt</th>
                <th style={thNumStyle}>Kostnad</th>
                <th style={thNumStyle}>TB1</th>
                <th style={thNumStyle}>TB1 %</th>
                <th style={thNumStyle}>TB1 per ärende</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <Fragment key={group.caseType}>
                  <tr
                    style={{
                      borderTop: '1px solid #ddd',
                      fontWeight: 'bold',
                    }}
                  >
                    <td style={tdStyle}>
                      {caseTypes[group.caseType] ?? group.caseType}
                    </td>
                    <Cells sums={group.total} />
                  </tr>
                  {group.counties.map(([county, sums]) => (
                    <tr
                      key={`${group.caseType}-${county}`}
                      style={{ borderBottom: '1px solid #f3f3f3' }}
                    >
                      <td style={{ ...tdStyle, paddingLeft: '24px' }}>
                        {county}
                      </td>
                      <Cells sums={sums} />
                    </tr>
                  ))}
                </Fragment>
              ))}
              <tr style={totalRowStyle}>
                <td style={tdStyle}>Totalt</td>
                <Cells sums={total} />
              </tr>
            </tbody>
          </table>
        </div>
        <ControlFigures
          items={[
            failedCases
              ? `${nf(failedCases)} ärenden saknade pris i prislistan och räknas med intäkt 0`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default TB1ByCaseTypeAndCountyWidget;
